import { isEmpty } from 'ramda'
import { ReactNode, MouseEvent, useRef, useEffect } from 'react'

import Spinner from '~/src/components/generic/Spinner'

import { tw } from '~/lib/utils'

type Props<T> = {
  results: Array<T>
  maxNumOptions: number
  highlightedRowIndex: number
  hasFocus: boolean
  searchString: string
  resultsClassName?: string
  isLoading?: boolean
  isSearching: boolean
  dropdownCtaLabel?: string
  onDropdownCta?: () => void
  onSelect: (value: T) => void
  renderOption: (value: T) => ReactNode
  setHighlightedRowIndex: (index: number) => void
}

const Results = <T,>({
  searchString,
  hasFocus,
  results,
  maxNumOptions,
  highlightedRowIndex,
  resultsClassName,
  isLoading,
  isSearching,
  dropdownCtaLabel,
  renderOption,
  onDropdownCta,
  onSelect,
  setHighlightedRowIndex,
}: Props<T>) => {
  const optionEls = useRef<Array<HTMLDivElement | null>>([])
  const optionsContainerEl = useRef<HTMLDivElement>(null)

  useEffect(() => {
    optionEls.current[highlightedRowIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  }, [highlightedRowIndex])

  useEffect(() => {
    if (optionsContainerEl.current && optionsContainerEl.current.scrollTop !== 0) {
      optionsContainerEl.current.scrollTop = 0
    }
    setHighlightedRowIndex(-1)
  }, [searchString, setHighlightedRowIndex])

  const showResults = hasFocus && results.length > 0 && !isLoading && !isSearching
  const noSearchResults =
    hasFocus && !isLoading && isEmpty(results) && !isSearching && searchString !== ''

  const loadingState = (isLoading || isSearching) && hasFocus && (
    <div className="flex h-56 items-center justify-center">
      <Spinner />
    </div>
  )

  const handleMouseDown = ({
    event,
    option,
  }: {
    event: MouseEvent<HTMLDivElement>
    option: T
  }) => {
    event.preventDefault()
    onSelect(option)
  }

  const handleLabelCtaClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    onDropdownCta?.()
  }

  return (
    <div
      className={tw(
        'absolute z-50 mt-0.5 flex w-full min-w-[100px] flex-col overflow-hidden rounded-b-md bg-white shadow-xl',
        resultsClassName
      )}
    >
      <div
        className="max-h-60 overflow-y-auto"
        data-testid="search-autocomplete-result"
        ref={optionsContainerEl}
      >
        {loadingState}
        {noSearchResults && (
          <div className="px-3 py-2 text-body-m">No results found</div>
        )}
        {showResults &&
          results.slice(0, maxNumOptions).map(
            (option: T, index): ReactNode => (
              <div
                className={tw(
                  'w-full cursor-pointer px-2 py-1 text-gray-900 hover:bg-gray-100 hover:text-gray-900',
                  index === highlightedRowIndex && 'bg-gray-100 text-gray-900'
                )}
                data-testid="search-list-item"
                key={index}
                ref={(el) => {
                  optionEls.current[index] = el
                }}
                onClick={(event) => handleMouseDown({ event, option })}
                onMouseDown={() => {
                  onSelect(option)
                }}
              >
                {renderOption(option)}
              </div>
            )
          )}
      </div>
      {dropdownCtaLabel && onDropdownCta && (showResults || noSearchResults) ? (
        <div
          className="flex cursor-pointer items-center justify-center rounded-none border-t border-divider py-2 text-label-s text-gray-900 underline-offset-2 hover:bg-gray-100"
          onMouseDown={handleLabelCtaClick}
        >
          {dropdownCtaLabel}
        </div>
      ) : null}
    </div>
  )
}

export default Results
